import GitHubIcon from "@mui/icons-material/GitHub";
import WarningIcon from "@mui/icons-material/Warning";
import { Chip, Link, Stack, Tooltip, Typography } from "@mui/material";

interface GitBranchChipProps {
  branch?: string;
  commitSha?: string;
  commitShaShort?: string;
  commitMessage?: string;
  remoteOrigin?: string;
  isDirty?: boolean;
  showCommit?: boolean;
}

/**
 * Parse git remote origin to extract GitHub owner/repo.
 * Handles both SSH (git@github.com:owner/repo.git) and HTTPS formats.
 */
function parseGitHubUrl(remoteOrigin: string): { owner: string; repo: string } | null {
  // SSH format: git@github.com:owner/repo.git
  const sshMatch = remoteOrigin.match(/git@github\.com:([^/]+)\/([^.]+)(?:\.git)?/);
  if (sshMatch) {
    return { owner: sshMatch[1], repo: sshMatch[2] };
  }

  // HTTPS format: https://github.com/owner/repo.git
  const httpsMatch = remoteOrigin.match(/https:\/\/github\.com\/([^/]+)\/([^.]+)(?:\.git)?/);
  if (httpsMatch) {
    return { owner: httpsMatch[1], repo: httpsMatch[2] };
  }

  return null;
}

function buildGitHubCommitUrl(remoteOrigin: string, commitSha: string): string | null {
  const parsed = parseGitHubUrl(remoteOrigin);
  if (!parsed) return null;
  return `https://github.com/${parsed.owner}/${parsed.repo}/commit/${commitSha}`;
}

function buildGitHubBranchUrl(remoteOrigin: string, branch: string): string | null {
  const parsed = parseGitHubUrl(remoteOrigin);
  if (!parsed) return null;
  return `https://github.com/${parsed.owner}/${parsed.repo}/tree/${branch}`;
}

export function GitBranchChip({
  branch,
  commitSha,
  commitShaShort,
  commitMessage,
  remoteOrigin,
  isDirty,
  showCommit = false,
}: GitBranchChipProps) {
  if (!branch) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const branchUrl = remoteOrigin ? buildGitHubBranchUrl(remoteOrigin, branch) : null;
  const commitUrl =
    remoteOrigin && commitSha ? buildGitHubCommitUrl(remoteOrigin, commitSha) : null;

  const tooltipContent = (
    <Stack spacing={0.5}>
      <Typography variant="body2">Branch: {branch}</Typography>
      {commitShaShort && <Typography variant="body2">Commit: {commitShaShort}</Typography>}
      {commitMessage && (
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {commitMessage}
        </Typography>
      )}
      {isDirty && (
        <Typography variant="body2" color="warning.main">
          Working directory has uncommitted changes
        </Typography>
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} arrow>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {branchUrl ? (
          <Link
            href={branchUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Chip
              icon={<GitHubIcon sx={{ fontSize: 14 }} />}
              label={branch}
              size="small"
              variant="outlined"
              clickable
            />
          </Link>
        ) : (
          <Chip label={branch} size="small" variant="outlined" />
        )}
        {isDirty && (
          <Tooltip title="Uncommitted changes">
            <WarningIcon sx={{ fontSize: 14, color: "warning.main" }} />
          </Tooltip>
        )}
        {showCommit &&
          commitShaShort &&
          (commitUrl ? (
            <Link href={commitUrl} target="_blank" rel="noopener noreferrer" underline="hover">
              <Typography variant="caption" color="text.secondary">
                {commitShaShort}
              </Typography>
            </Link>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {commitShaShort}
            </Typography>
          ))}
      </Stack>
    </Tooltip>
  );
}
